import {
	type BaseQueryFn,
	type FetchArgs,
	fetchBaseQuery,
	type FetchBaseQueryError,
	type FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query/react';

import { getUserData } from '@/constants/global';
import { isAuthPath, recoverFromUnauthorized } from '@/session/reauth';

export type CoreBaseQuery = BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, object, FetchBaseQueryMeta>;

const urlOf = (args: string | FetchArgs) => (typeof args === 'string' ? args : args.url);

/**
 * The base query every core-token RTK slice shares.
 *
 * Attaches the bearer, and on a 401 applies the same recovery as the axios clients
 * (`src/session/reauth.ts`): adopt a rotation another tab made, else refresh once, then
 * re-run the request. Before this the slices had no 401 handling at all, so an expired
 * token surfaced as a generic error state on whichever page fired first. `/Auth/*` is
 * skipped — `coreApiSlice` hosts login/OTP/logout, whose 401s are not session expiry.
 */
export const createCoreBaseQuery = (baseUrl: string): CoreBaseQuery => {
	const raw = fetchBaseQuery({
		baseUrl,
		prepareHeaders: (headers) => {
			const token = getUserData().token;
			if (token) {
				headers.set('Authorization', `Bearer ${token}`);
			}
			return headers;
		},
	});

	return async (args, api, extraOptions) => {
		const sentToken = getUserData().token;
		const result = await raw(args, api, extraOptions);
		if (result.error?.status !== 401 || isAuthPath(urlOf(args))) return result;

		const decision = await recoverFromUnauthorized(sentToken);
		return decision === 'replay' ? raw(args, api, extraOptions) : result;
	};
};
